import { FunctionComponent, memo, useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { faker } from "@faker-js/faker";
import {
    useCreateDashboardStore,
    ColorPicker,
    getMQTTQueryByDevice,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
} from ".";
import DashboardCreateSelectSensorModal from "./modal/SelectDevices";
import CreateChartModal from "./modal/CreateChart";
import { COLORS, DASHBOARD_CHART_MAX } from "constant";
import { displaySensorType, parseColorToHex } from "utils/functions";
import CloseIcon from "@mui/icons-material/Close";
import { getSensorType } from "utils/sensorFunctions";
import { SensorType } from "constant/gateway";

import {
    BlueRadio,
    BlueText,
    SelectedGreenBox,
    ColorPickerBackground,
} from "components/common";
import { debounce, get, isEmpty, omit, set, size, slice } from "lodash";

const SingleCreateData: CreateChartState = {
    title: "Creating Consumption Charts...",
    data: [],
    columns: [
        {
            key: "chart_name",
            name: "Chart Name",
        },
        {
            key: "device_name",
            name: "Sensor",
        },
        {
            key: "sensor_type",
            name: "Type",
        },
        {
            key: "gatewayName",
            name: "Gateway",
        },
        {
            key: "ldsu_uuid",
            name: "LDSU UUID",
            className: "flex-basis-220px",
            render: (data: any) => (
                <div className="text-nowrap text-white">{data.ldsu_uuid}</div>
            ),
        },
        {
            key: "color",
            name: "Color",
            render: (data: any) => (
                <div className="color-picker__toggle">
                    <ColorPickerBackground
                        $inputColor={parseColorToHex(data.color)}
                    />
                </div>
            ),
        },
    ],
    transform: (data: any) => ({
        attributes: [
            {
                color: data.color,
                device_id: data.ldsu_uuid,
                gateway_id: data.gatewayId,
                name: data.sensor_type,
                said: data.SAID,
                show_alert: data.alert || false,
                time_series: getMQTTQueryByDevice(data),
                unit: data.unit,
            },
        ],
        source: "AGGREGATION",
        name: data.chart_name || "Consumption Chart",
        chart: "BAR",
        time_range: "1d",
    }),
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.device_id}.chart_name"]`,
            isValid: CHART_NAME_PATTERN.test(item.chart_name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

const CombinedCreateData: CreateChartState = {
    title: "Creating Combined Chart...",
    multiple: false,
    minCombinedSensors: 2,
    data: [],
    columns: [
        {
            key: "chart_name",
            name: "Chart Name",
        },
        {
            key: "type",
            name: "Sensor Type",
            render: (data: any) => data.attributes?.[0].sensor_type,
        },
        {
            key: "sensors_count",
            name: "No. of Sensors",
            render: (data: any) => data.attributes?.length,
        },
    ],
    transform: (data: any) => ({
        attributes: data.attributes.map((d: any) => ({
            color: d.color,
            device_id: d.ldsu_uuid,
            gateway_id: d.gatewayId,
            name: d.sensor_type,
            said: String(d.SAID),
            show_alert: false,
            time_series: getMQTTQueryByDevice(d),
            unit: d.unit,
        })),
        source: "AGGREGATION",
        chart: "COMBINED",
        name: data.chart_name || "Combined Chart",
    }),
    validate() {
        const validates = this.data.map((item) => ({
            path: "",
            isValid:
                item.chart_name &&
                CHART_NAME_PATTERN.test(item.chart_name?.trim()),
            message: CHART_ERROR_MESSAGE,
        }));

        if (this.data[0].attributes.length < 2) {
            validates.push({
                path: "",
                isValid: false,
                message:
                    "Please select at least 2 sensors to create a combined chart.",
            });
        }

        return validates;
    },
};

const ConsumptionChartTypeMapping = {
    EnergyConsumption: "Energy",
    WaterConsumption: "Water",
};

const SensorTypeStringMapping = {
    EnergyConsumption: "active power",
    WaterConsumption: "water flow",
};

export type ChartType = keyof typeof ConsumptionChartTypeMapping;

enum ConsumptionChartType {
    single = "Individual",
    combined = "Combined",
}

const EnergyConsumptionSAID = new Set([4, 5, 10, 15]);
const WaterConsumptionSAID = new Set([8]);

const FilterFn = {
    EnergyConsumption: (sensor: any) => {
        return (
            getSensorType(sensor) === SensorType.EnergyConsumption &&
            EnergyConsumptionSAID.has(sensor.SAID)
        );
    },

    WaterConsumption: (sensor: any) => {
        return (
            getSensorType(sensor) === SensorType.WaterConsumption &&
            WaterConsumptionSAID.has(sensor.SAID)
        );
    },
};

const CreateConsumptionComponent: FunctionComponent = () => {
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<{
        [key: string]: any;
    }>({});
    const [consumptionChartType, setConsumptionChartType] = useState(
        ConsumptionChartType.single
    );
    const [chartName, setChartName] = useState("Combined Chart");
    const {
        createModalShow,
        setCreateModalShow,
        createData,
        deviceList,
        chartType,
        loading,
    } = useCreateDashboardStore();

    const chartTypeName = ConsumptionChartTypeMapping[chartType as ChartType];
    const sensorType = SensorTypeStringMapping[chartType as ChartType];
    const title = `Sensors for ${consumptionChartType} - ${chartTypeName} consumption chart`;
    const isCombined = consumptionChartType === ConsumptionChartType.combined;
    const filterSensor = FilterFn[chartType as ChartType];

    const chartOptions = [
        {
            title: "Individual Consumption Charts",
            description: `Create separate consumption charts for each ${sensorType} sensor.`,
            key: ConsumptionChartType.single,
        },
        {
            title: "Combined Consumption Chart",
            description: `Data from multiple ${sensorType} sensors onto a single consumption chart for a comprehensive overview.`,
            key: ConsumptionChartType.combined,
        },
    ];

    useEffect(() => {
        const name = `Combined ${chartTypeName} Consumption`;
        setChartName(name);
    }, [chartType, chartTypeName]);

    useEffect(() => {
        if (!size(selectedDevices)) {
            return;
        }

        if (isCombined) {
            const _selectedDevices = slice(
                Object.values(selectedDevices),
                0,
                8
            );

            setSelectedDevices(_selectedDevices.pluck("device_id"));
        } else {
            const data = getData();
            useCreateDashboardStore.setState({
                createData: {
                    ...SingleCreateData,
                    data: data,
                    multiple: false,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consumptionChartType]);

    useEffect(() => {
        if (size(selectedDevices)) {
            const data = getData();
            useCreateDashboardStore.setState({
                createData: {
                    ...(isCombined ? CombinedCreateData : SingleCreateData),
                    data: data,
                    multiple: isCombined,
                },
            });
        } else {
            useCreateDashboardStore.setState({
                createData: undefined,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDevices, chartName]);

    useEffect(() => {
        if (!loading && deviceList.length > 0) {
            const meteringSensors = deviceList.filter((sensor) =>
                filterSensor(sensor)
            );
            useCreateDashboardStore.setState({
                deviceList: meteringSensors,
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const getData = () =>
        !isCombined
            ? Object.values(selectedDevices)
            : [
                  {
                      chart_name: chartName,
                      attributes: Object.values(selectedDevices),
                  },
              ];

    const getChartName = (sensor: any) => {
        const sensorType = getSensorType(sensor);
        return `${
            sensorType === SensorType.WaterConsumption ? "Water" : "Energy"
        } Consumption`;
    };

    const onRemove = (device: any) => {
        setSelectedDevices((prev: any) => omit(prev, device.device_id));
    };

    const onChartNameChange = debounce((event) => {
        const { name, value } = event.target;
        setSelectedDevices((prev) => set(prev, name, value));
    }, 500);
    return (
        <>
            <Row>
                <Col sm={12}>
                    <p>{chartTypeName} consumption chart:</p>
                </Col>
                <Col md={12}>
                    <div>
                        {chartOptions.map(({ key, title, description }) => {
                            return (
                                <SelectedGreenBox
                                    className={`mt-2 w-100 ${
                                        key === consumptionChartType &&
                                        "selected"
                                    }`}
                                    key={key}
                                    onClick={() => {
                                        setConsumptionChartType(key);
                                    }}
                                >
                                    <BlueRadio
                                        name="chart-type"
                                        custom
                                        type="radio"
                                        value={key}
                                        id={`chart-type-${key}`}
                                        checked={key === consumptionChartType}
                                        label={
                                            <div className="d-flex align-items-center ml-2">
                                                <div className="ml-2">
                                                    <span className="d-block">
                                                        {title}
                                                    </span>
                                                    <BlueText>
                                                        {description}
                                                    </BlueText>
                                                </div>
                                            </div>
                                        }
                                        onChange={() => {
                                            setConsumptionChartType(key);
                                        }}
                                    />
                                </SelectedGreenBox>
                            );
                        })}
                    </div>
                    <hr className="my-4" />
                </Col>
            </Row>
            <Row>
                <Col sm="12">
                    {isCombined && (
                        <Form.Group>
                            <Form.Label>Chart Name</Form.Label>
                            <Form.Control
                                name="name"
                                type="text"
                                value={chartName}
                                placeholder=""
                                onChange={(event) =>
                                    setChartName(event.target.value)
                                }
                            />
                        </Form.Group>
                    )}
                    <p>{title}</p>
                    <div className="mt-2">
                        <Button
                            className="mr-2"
                            variant="primary"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Sensors
                        </Button>
                        {isCombined && (
                            <BlueText>You can add max 8 sensors</BlueText>
                        )}
                    </div>
                    {isCombined && size(selectedDevices) === 1 && (
                        <p className="mt-2 text-danger">
                            Please select at least two sensors to create a
                            combined consumption chart.
                        </p>
                    )}

                    <DashboardCreateSelectSensorModal
                        show={showSelectModal}
                        allowSensorFilter={false}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedDevices}
                        onSave={(event, data) =>
                            setSelectedDevices(
                                Object.values(data)
                                    .map((item: any, index: number) => ({
                                        ...item,
                                        chart_name: getChartName(item),
                                        color:
                                            COLORS[index] || faker.color.rgb(),
                                        alert: false,
                                    }))
                                    .pluck("device_id")
                            )
                        }
                        deviceType="Sensor"
                        maxDevices={isCombined ? 8 : DASHBOARD_CHART_MAX}
                    />
                </Col>
            </Row>
            <Row className="mt-4 cstm-table">
                <Col sm={12}>
                    {!!Object.values(selectedDevices).length && (
                        <div className="table-head">
                            <Row className="no-checkbox">
                                <Col md={2}>Sensor</Col>
                                {!isCombined && <Col md={2}>Chart Name</Col>}
                                <Col md={2}>Type</Col>
                                <Col md={isCombined ? 4 : 2} sm={2}>
                                    Gateway
                                </Col>
                                <Col md={2}>LDSU UUID</Col>
                                <Col lg={1} md={1} sm={2}>
                                    Color
                                </Col>
                                <Col sm={1}></Col>
                            </Row>
                        </div>
                    )}
                    {Object.values(selectedDevices).map((device, index) => (
                        <div
                            key={getId()}
                            id={device.device_id}
                            className="table-row bg-secondary"
                        >
                            <Row className="no-checkbox">
                                <Col md={2}>{device.device_name}</Col>
                                {!isCombined && (
                                    <Col md={2}>
                                        <Form.Control
                                            name={`${device.device_id}.chart_name`}
                                            type="text"
                                            defaultValue={device.chart_name}
                                            placeholder=""
                                            onChange={onChartNameChange}
                                        />
                                    </Col>
                                )}
                                <Col md={2}>{displaySensorType(device)}</Col>
                                <Col md={isCombined ? 4 : 2} sm={2}>
                                    {device.gatewayName}
                                </Col>
                                <Col md={2}>{device.ldsu_uuid}</Col>
                                <Col sm={2} lg={1}>
                                    <ColorPicker
                                        defaultValue={get(
                                            selectedDevices,
                                            `${device.device_id}.color`,
                                            COLORS[index]
                                        )}
                                        onChange={(event, color) =>
                                            setSelectedDevices((prev: any) =>
                                                set(
                                                    prev,
                                                    `${device.device_id}.color`,
                                                    color
                                                )
                                            )
                                        }
                                    />
                                </Col>
                                {
                                    <Col
                                        sm={1}
                                        className="d-flex justify-content-end"
                                    >
                                        <CloseIcon
                                            className="cursor-pointer mr-2 text-primary-red-1"
                                            onClick={() => onRemove(device)}
                                            onKeyDown={() => onRemove(device)}
                                        />
                                    </Col>
                                }
                            </Row>
                        </div>
                    ))}
                </Col>
            </Row>

            <CreateChartModal
                show={createModalShow && !isEmpty(createData)}
                onHide={() => setCreateModalShow(false)}
            />
        </>
    );
};

export default memo(CreateConsumptionComponent);
